// LAP — Modale d'ajout au portefeuille depuis une page détail Marché.
// Reçoit les actifs sélectionnés dans le tableau, laisse choisir le type
// d'investissement + la quantité par actif, puis crée les positions via
// /api/investment/portfolio-investment-operations/.
import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { makeGetRequest, makePostRequest } from 'utils/api'

// Les lignes du tableau Marché n'ont pas toutes les mêmes champs selon le
// marché (actions, obligations, ETF, forex, alternatifs…). On dérive un nom
// et un prix de façon défensive.
export const assetName = (a = {}) =>
  a.name ||
  a.modele ||
  [a.marque, a.modele].filter(Boolean).join(' ').trim() ||
  a.symbol ||
  (a.base_currency && a.target_currency
    ? `${a.base_currency}/${a.target_currency}`
    : a.pair) ||
  '—'

export const assetPrice = (a = {}) => {
  const raw = a.price ?? a.exchange_rate ?? a.exchangeRate
  const n = parseFloat(raw)
  return isNaN(n) ? 0 : n
}

// Extrait un message lisible d'une réponse d'erreur Axios/DRF.
export const describeError = (res) => {
  if (!res) return 'réseau'
  if (res.status === 0) return 'réseau'
  const d = res.data
  if (d && typeof d === 'object') {
    if (d.detail) return `${res.status} · ${d.detail}`
    const key = Object.keys(d)[0]
    if (key) {
      const val = Array.isArray(d[key]) ? d[key][0] : d[key]
      return `${res.status} · ${key}: ${val}`
    }
  }
  return `HTTP ${res.status || '?'}`
}

const cap = (v, n) => String(v == null ? '' : v).slice(0, n)

export default function AddToPortfolioDialog({ open, assets = [], onClose, onAdded }) {
  const { t } = useTranslation('layout')
  const [types, setTypes] = useState([])
  const [typeId, setTypeId] = useState('')
  const [qty, setQty] = useState({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open) return
    const q = {}
    assets.forEach((a) => {
      q[a.id] = '1'
    })
    setQty(q)
    const fetchTypes = async () => {
      const { data, status } = await makeGetRequest(
        '/api/investment/investment-type/',
        {},
        true,
      )
      if (status === 200 && Array.isArray(data)) {
        setTypes(data)
        setTypeId((prev) => prev || (data[0] && data[0].id) || '')
      }
    }
    fetchTypes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleConfirm = async () => {
    if (!typeId) {
      toast.error(
        t('investments.detail.addDialog.typeRequired', 'Choisissez un type d’investissement'),
      )
      return
    }
    setSubmitting(true)
    let ok = 0
    let fail = 0
    let lastErr = ''
    for (const a of assets) {
      const price = assetPrice(a)
      const quantity = parseFloat(qty[a.id])
      const fd = new FormData()
      fd.append('type', typeId)
      fd.append('name', cap(assetName(a), 255))
      // PortfolioInvestment.description est limité à 500 caractères : on n'y
      // recopie PAS la description de l'actif (souvent un long texte société),
      // sinon le backend renvoie une erreur de validation.
      fd.append('description', cap(a.sector || a.type || '', 480))
      fd.append('sector', cap(a.sector || a.type || '', 255))
      fd.append('industry', cap(a.industry || '', 255))
      fd.append('quantity', quantity > 0 ? quantity : 1)
      fd.append('current_price', price)
      fd.append('average_price', price)
      fd.append('annual_income', 0)
      // eslint-disable-next-line no-await-in-loop
      const res = await makePostRequest(
        '/api/investment/portfolio-investment-operations/',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
        true,
      )
      if (res && res.status === 201) ok += 1
      else {
        fail += 1
        lastErr = describeError(res)
      }
    }
    setSubmitting(false)
    if (ok) {
      toast.success(
        t('investments.detail.addDialog.added', 'Actif(s) ajouté(s) à votre portefeuille'),
      )
      if (onAdded) onAdded(ok)
      onClose()
    }
    if (fail && !ok) {
      toast.error(
        `${t('investments.detail.addDialog.failed', 'Échec de l’ajout au portefeuille')} — ${lastErr}`,
      )
    } else if (fail) {
      toast.warning(
        `${t(
          'investments.detail.addDialog.partialFail',
          'Certains actifs n’ont pas pu être ajoutés',
        )} — ${lastErr}`,
      )
    }
  }

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={submitting ? undefined : onClose}>
      <DialogTitle sx={{ color: 'black' }}>
        {t('investments.detail.addDialog.title', 'Ajouter au portefeuille')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', mb: 2 }}>
          {t(
            'investments.detail.addDialog.intro',
            'Choisissez le type d’investissement et la quantité pour chaque actif.',
          )}
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="add-portfolio-type-label">
            {t('investments.detail.addDialog.type', 'Type d’investissement')}
          </InputLabel>
          <Select
            labelId="add-portfolio-type-label"
            label={t('investments.detail.addDialog.type', 'Type d’investissement')}
            value={typeId}
            onChange={(e) => setTypeId(e.target.value)}
          >
            {types.map((ty) => (
              <MenuItem key={ty.id} value={ty.id}>
                {ty.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {assets.map((a) => (
          <Box
            key={a.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              py: 1,
              borderTop: '1px solid rgba(0,0,0,0.08)',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                noWrap
                sx={{ fontWeight: 600, color: 'black', fontSize: 14 }}
              >
                {assetName(a)}
              </Typography>
              <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.55)' }}>
                {t('investments.detail.addDialog.price', 'Prix')}:{' '}
                {assetPrice(a).toLocaleString('fr-FR', { maximumFractionDigits: 2 })}
              </Typography>
            </Box>
            <TextField
              type="number"
              size="small"
              label={t('investments.detail.addDialog.quantity', 'Quantité')}
              value={qty[a.id] ?? '1'}
              onChange={(e) => setQty((q) => ({ ...q, [a.id]: e.target.value }))}
              inputProps={{ min: 0, step: 'any' }}
              sx={{ width: 120, flexShrink: 0 }}
            />
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('investments.detail.addDialog.cancel', 'Annuler')}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting || assets.length === 0}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {t('investments.detail.addDialog.confirm', 'Ajouter')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
